import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function EmailSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [recapSending, setRecapSending] = useState(false);
  const [lastRecap, setLastRecap] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email/settings");
      setCfg(data);
      setSender(data.sender_email || "");
      setLastRecap(data.last_recap_result || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const saveSender = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email/settings", { sender_email: sender });
      setCfg(data);
      toast.success("Sender email saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleRecap = async (enabled) => {
    try {
      const { data } = await api.put("/admin/email/settings", { weekly_recap_enabled: enabled });
      setCfg(data);
      toast.success(`Weekly recap ${enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email/test");
      setCfg((c) => ({ ...c, last_email_test: data }));
      if (data.ok) {
        toast.success(`Test email sent to ${data.to}`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown error"}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const runRecap = async (dry) => {
    setRecapSending(true);
    try {
      const { data } = await api.post(`/admin/email/recap?dry_run=${dry ? "true" : "false"}&force=${dry ? "false" : "true"}`);
      setLastRecap(data);
      if (data.skipped) {
        toast.info("Weekly recap is disabled in settings");
      } else if (dry) {
        toast.info(`Dry run · ${data.photographers_with_sales} photographer${data.photographers_with_sales === 1 ? "" : "s"} with sales this week`);
      } else {
        toast.success(`${data.emails_sent} recap email${data.emails_sent === 1 ? "" : "s"} sent`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecapSending(false);
    }
  };

  if (!cfg) {
    return <div className="border-t border-white/10 pt-10"><p className="text-white/50 text-sm">Loading email settings…</p></div>;
  }

  const test = cfg.last_email_test;

  return (
    <div className="border-t border-white/10 pt-10 space-y-8" data-testid="email-settings">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl">Email delivery</h3>
          <p className="text-sm text-white/50 mt-1 max-w-2xl">
            Expiry warnings, magic extension links and weekly sales recaps are sent through Resend.
          </p>
        </div>
        <span
          className={`text-xs uppercase tracking-[0.3em] px-3 py-1 border ${cfg.resend_configured ? "text-green-400 border-green-400/40" : "text-red-400 border-red-400/40"}`}
          data-testid="email-status-badge"
        >
          {cfg.resend_configured ? "Resend connected" : "Resend key missing"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <form onSubmit={saveSender} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Sender address</label>
            <input
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="no-reply@photomoto.co.uk"
              className="input-minimal"
              data-testid="sender-email-input"
            />
          </div>
          {cfg.sender_is_test && (
            <p className="text-xs pm-orange font-condensed" data-testid="test-sender-warning">
              Using Resend&apos;s test sender — emails only deliver to your own Resend account address.
              Verify <span className="font-semibold">photomoto.co.uk</span> at resend.com/domains, then switch
              the sender to no-reply@photomoto.co.uk.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-white text-black px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
              data-testid="sender-email-save"
            >
              {saving ? "Saving…" : "Save sender"}
            </button>
            <button
              type="button"
              onClick={sendTest}
              disabled={testing || !cfg.resend_configured}
              className="border border-white/20 hover:border-white/60 px-6 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
              data-testid="send-test-email"
            >
              {testing ? "Sending…" : "Send test email"}
            </button>
          </div>
          {test && (
            <p className={`text-xs font-condensed ${test.ok ? "text-green-400" : "text-red-400"}`} data-testid="last-test-result">
              Last test · {new Date(test.at).toLocaleString()} · {test.ok ? `delivered to ${test.to}` : `failed: ${test.error}`}
            </p>
          )}
        </form>

        <div className="space-y-4">
          <label className="flex items-center gap-3 text-sm text-white/70 font-condensed">
            <input
              type="checkbox"
              checked={!!cfg.weekly_recap_enabled}
              onChange={(e) => toggleRecap(e.target.checked)}
              className="accent-[var(--pm-orange)]"
              data-testid="weekly-recap-toggle"
            />
            Send photographers a weekly sales recap (photos sold, earnings, best-selling event)
          </label>
          <p className="text-xs text-white/40 font-condensed">
            Goes out automatically with the first scheduled run of each week. Only photographers with paid sales in the last 7 days receive one.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => runRecap(true)}
              disabled={recapSending}
              className="border border-white/20 hover:border-white/60 px-6 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
              data-testid="recap-dry-run"
            >
              {recapSending ? "…" : "Recap · dry run"}
            </button>
            <button
              onClick={() => runRecap(false)}
              disabled={recapSending || !cfg.resend_configured}
              className="border border-[var(--pm-orange)] pm-orange px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange)] hover:text-black disabled:opacity-50"
              data-testid="recap-send-now"
            >
              {recapSending ? "Sending…" : "Send recap now"}
            </button>
          </div>
          {lastRecap && !lastRecap.skipped && (
            <p className="text-xs text-white/60 font-condensed" data-testid="last-recap-result">
              {lastRecap.dry_run ? "Dry run" : "Last recap"} · week {lastRecap.week_key} · {lastRecap.photographers_with_sales} photographer{lastRecap.photographers_with_sales === 1 ? "" : "s"} with sales · {lastRecap.emails_sent} sent · {lastRecap.emails_failed} failed
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
